import {
  Alert,
  AlertIcon,
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  InputProps,
  SimpleGrid,
  Switch,
  Text,
  useToast,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import Card from 'components/card/Card';
import CustomButton from 'components/CustomButton';
import FormLabelCustom from 'components/FormLabel';
import InputMaskDocument from 'components/InputMaskDocument';
import { queryClient } from 'index';
import { forwardRef, useState } from 'react';
import { Control, Controller, Path, useForm, useWatch } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import { useNavigate } from 'react-router-dom';
import { accountFeatures, createCarnet } from 'services/api.service';
import { formatCurrency } from 'utils/number';
import {
  apiErrorMessage,
  buildCarnetBody,
  CARNETS_QUERY_KEY,
  newIdempotencyKey,
  protestDescription,
  protestFeePerBillet,
} from './carnet';
import { CreateCarnetValues, createCarnetSchema, MAX_INSTALLMENTS, MIN_INSTALLMENTS, todayIsoDate } from './schema';

const DEFAULTS = {
  installments: 12,
  firstDueDate: '',
  description: '',
  payerName: '',
  payerDocument: '',
  payerEmail: '',
  latePenaltyPercent: '',
  lateMonthlyInterestPercent: '',
  withProtest: false,
};

/**
 * Campo de texto do formulário. Local, em vez do `InputForm` compartilhado:
 * aquele põe `required` em todo campo e não tipa `min`/`max`/`maxLength`, e aqui
 * há campos opcionais e limites que o navegador ajuda a respeitar.
 */
function CarnetField({
  control,
  name,
  label,
  helper,
  ...inputProps
}: {
  control: Control<CreateCarnetValues>;
  name: Path<CreateCarnetValues>;
  label: string;
  helper?: string;
} & Omit<InputProps, 'name' | 'value' | 'onChange' | 'onBlur'>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
        <FormControl isInvalid={!!error}>
          <FormLabelCustom label={label} name={name} props={{}} />
          <Input
            id={name}
            name={name}
            size="sm"
            fontSize="md"
            bg="white"
            value={(value as string | number | undefined) ?? ''}
            onChange={onChange}
            onBlur={onBlur}
            {...inputProps}
          />
          {helper && !error && <FormHelperText>{helper}</FormHelperText>}
          <FormErrorMessage>{error?.message}</FormErrorMessage>
        </FormControl>
      )}
    />
  );
}

// O <Input> do Chakra já com o estilo dos outros campos, para o NumericFormat
// usar como `customInput`.
const MoneyInput = forwardRef<HTMLInputElement, InputProps>((props, ref) => (
  <Input ref={ref} size="sm" fontSize="md" bg="white" {...props} />
));
MoneyInput.displayName = 'MoneyInput';

/**
 * Campo de valor em reais. Local, em vez do `InputNumber` compartilhado: aquele
 * fixa altura e cantos retos por `style`, e ao lado dos outros campos desta tela
 * ficava com outra cara. A formatação é a mesma (R$, vírgula decimal, 2 casas).
 */
function CarnetMoneyField({
  control,
  name,
  label,
  placeholder,
}: {
  control: Control<CreateCarnetValues>;
  name: Path<CreateCarnetValues>;
  label: string;
  placeholder?: string;
}) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
        <FormControl isInvalid={!!error}>
          <FormLabelCustom label={label} name={name} props={{}} />
          <NumericFormat
            id={name}
            name={name}
            // O NumericFormat tipa `customInput` pelos atributos nativos do
            // <input>, onde `size` é número; no Chakra é 'sm' | 'md'.
            customInput={MoneyInput as any}
            inputMode="decimal"
            prefix="R$ "
            decimalScale={2}
            fixedDecimalScale
            decimalSeparator=","
            thousandSeparator="."
            allowNegative={false}
            placeholder={placeholder}
            value={(value as number | undefined) ?? ''}
            // Vazio vira undefined, e o schema pede o valor.
            onValueChange={(values) => onChange(values.floatValue)}
            onBlur={onBlur}
          />
          <FormErrorMessage>{error?.message}</FormErrorMessage>
        </FormControl>
      )}
    />
  );
}

export default function NewCarnet() {
  const toast = useToast();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  // Uma chave por formulário aberto, fixa entre tentativas: se o primeiro POST
  // chegou ao servidor e a resposta se perdeu, o segundo devolve o mesmo lote em
  // vez de registrar outros N boletos tarifados. O inicializador roda uma vez.
  const [idempotencyKey] = useState(() => newIdempotencyKey());

  const { control, handleSubmit } = useForm<CreateCarnetValues>({
    resolver: zodResolver(createCarnetSchema),
    defaultValues: DEFAULTS,
  });

  // O input de quantidade entrega texto: o resumo converte, o schema também.
  const amount = Number(useWatch({ control, name: 'amountPerInstallment' })) || 0;
  const installments = Number(useWatch({ control, name: 'installments' })) || 0;
  const firstDueDate = useWatch({ control, name: 'firstDueDate' });

  // Mesma chave das Configurações, então na prática sai do cache. Não bloqueia
  // o formulário: sem resposta, os placeholders caem no padrão do sistema, que
  // é o que o servidor usaria de todo jeito, e o protesto fica escondido.
  const { data: features } = useQuery(['features'], accountFeatures, { staleTime: 5 * 60 * 1000 });

  // Só `=== true` libera. A condição se repete no envio (buildCarnetBody).
  const protestAvailable = features?.allowBilletProtest === true;
  const feePerBillet = protestFeePerBillet(features?.billetProtestFee);

  const onSubmit = handleSubmit(async (values) => {
    setSubmitting(true);
    try {
      const { batchId } = await createCarnet(buildCarnetBody(values, { protestAvailable }), idempotencyKey);
      queryClient.invalidateQueries({ queryKey: [CARNETS_QUERY_KEY] });
      navigate(`/access/carnes/${batchId}`, { replace: true });
    } catch (err) {
      toast({
        title: 'Erro ao emitir carnê',
        description: apiErrorMessage(err, 'Não foi possível emitir o carnê.'),
        status: 'error',
        duration: 6000,
        position: 'top',
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  });

  return (
    <Box>
      <Card p="20px" mb={5}>
        <Text fontWeight="bold" color="navy.700" mb={4}>
          Parcelas
        </Text>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <CarnetMoneyField
            control={control}
            name="amountPerInstallment"
            label="Valor de cada parcela"
            placeholder="R$ 0,00"
          />
          <CarnetField
            control={control}
            name="installments"
            label="Quantidade de parcelas"
            type="number"
            inputMode="numeric"
            min={MIN_INSTALLMENTS}
            max={MAX_INSTALLMENTS}
            placeholder="2 a 24"
          />
          <CarnetField
            control={control}
            name="firstDueDate"
            label="Vencimento da 1ª parcela"
            type="date"
            min={todayIsoDate()}
          />
          <CarnetField
            control={control}
            name="description"
            label="Descrição"
            maxLength={80}
            placeholder="Mensalidade, parcela do curso..."
          />
        </SimpleGrid>
      </Card>

      {protestAvailable && (
        <Card p="20px" mb={5}>
          <Controller
            control={control}
            name="withProtest"
            render={({ field: { value, onChange } }) => (
              <FormControl display="flex" alignItems="flex-start" gap={4}>
                <Switch
                  id="withProtest"
                  colorScheme="brand"
                  mt={1}
                  isChecked={value === true}
                  onChange={(event) => onChange(event.target.checked)}
                />
                <Box>
                  <FormLabel htmlFor="withProtest" mb={1} fontWeight="bold" color="navy.700">
                    Registrar com protesto
                  </FormLabel>
                  <Text fontSize="sm" color="gray.600">
                    {protestDescription(feePerBillet, installments)}
                  </Text>
                </Box>
              </FormControl>
            )}
          />
        </Card>
      )}

      <Card p="20px" mb={5}>
        <Text fontWeight="bold" color="navy.700">
          Dados do pagador
        </Text>
        <Text fontSize="sm" color="gray.500" mb={4}>
          O boleto precisa do CPF/CNPJ do pagador.
        </Text>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
          <CarnetField
            control={control}
            name="payerName"
            label="Nome do pagador"
            maxLength={80}
            placeholder="Nome completo"
          />
          <InputMaskDocument
            control={control}
            name="payerDocument"
            label="CPF/CNPJ do pagador"
            type="text"
            placeholder="000.000.000-00"
          />
          <CarnetField
            control={control}
            name="payerEmail"
            label="E-mail (opcional)"
            type="email"
            placeholder="exemplo@email.com"
          />
        </SimpleGrid>
      </Card>

      <Card p="20px" mb={5}>
        <Text fontWeight="bold" color="navy.700">
          Juros e multa (opcional)
        </Text>
        <Text fontSize="sm" color="gray.500" mb={4}>
          Em branco usa o padrão da sua conta. Zero isenta.
        </Text>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <CarnetField
            control={control}
            name="latePenaltyPercent"
            label="Multa (%) — até 2%"
            inputMode="decimal"
            placeholder={String(features?.billetLateCharges?.penaltyPercent ?? 2).replace('.', ',')}
          />
          <CarnetField
            control={control}
            name="lateMonthlyInterestPercent"
            label="Juros ao mês (%) — até 1%"
            inputMode="decimal"
            placeholder={String(features?.billetLateCharges?.monthlyInterestPercent ?? 1).replace('.', ',')}
          />
        </SimpleGrid>
      </Card>

      {/* O custo aparece aqui, antes do botão: cada parcela é um registro
          bancário tarifado, pago ou não. `formatCurrency`, e não `maskCurrency`:
          é a conferência antes de confirmar, e esconder o número aqui só leva a
          pessoa a emitir no escuro. */}
      <Card p="20px" mb={5}>
        <Text fontWeight="bold" color="navy.700" fontSize="lg">
          {installments}× {formatCurrency(amount)}
        </Text>
        <Text fontSize="sm" color="gray.600">
          Total {formatCurrency(amount * installments)} · vencendo todo dia{' '}
          {firstDueDate ? firstDueDate.slice(8, 10) : '—'}
        </Text>
        <Alert status="warning" variant="left-accent" borderRadius="12px" mt={3}>
          <AlertIcon />
          <Text fontSize="sm">
            Serão registrados {installments} boletos no banco. Cada registro é tarifado, mesmo que o boleto não seja
            pago.
          </Text>
        </Alert>
      </Card>

      <Flex justify="flex-end" gap={3} direction={{ base: 'column', md: 'row' }}>
        <CustomButton variant="outline" onClick={() => navigate('/access/carnes')}>
          Voltar
        </CustomButton>
        <CustomButton variant="solid" isLoading={submitting} onClick={() => onSubmit()}>
          Emitir carnê
        </CustomButton>
      </Flex>
    </Box>
  );
}
